import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import Button from './Button';
import styles from '../sass/components/CreateAccountPrompt.module.scss';
import { api } from '../util/api';
import { mp } from '../util/analytics';
import { useAppDispatch } from '../app/hooks';
import { login } from '../features/auth/authSlice';
import { authenticate } from '../routes/Login';
import TextInput from './TextInput';
import {
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_FORGOT_PASSWORD,
} from '../util/constants';

interface CreateAccountPromptProps {
  email: string;
}

interface ExistingAccountPromptProps {
  email: string;
  /** Called once the customer has logged in from here. */
  onLoggedIn?: () => void;
}

/**
 * Shown after a booking made without logging in whose email already has an
 * account here. The booking is already saved (on that account); this only
 * OFFERS a login to see it, never requires one. Nothing of the account is
 * shown before the password is right.
 */
export function ExistingAccountPrompt({
  email,
  onLoggedIn,
}: ExistingAccountPromptProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!password) return;
    setError('');
    setStatus('sending');
    authenticate(
      { email, password },
      (token, roles, name, id, franchiseId, franchiseIds, vendorId) => {
        // Before the login lands: the page keeps this note (now saying
        // "logged in") instead of dropping it the moment it re-renders.
        setStatus('done');
        if (onLoggedIn) onLoggedIn();
        dispatch(login({
          email, token, roles, name, id, franchiseId, franchiseIds, vendorId,
        }));
        mp.track('Booking - Logged In After Booking');
      },
      (message) => {
        setStatus('idle');
        setError(message || t('account.prompt.wrongPassword'));
      },
      () => {
        setStatus('idle');
        setError(t('account.prompt.loginFailed'));
      },
    );
  };

  return (
    <div
      className={cx(styles.prompt, 'apr-account-note')}
      data-testid="existing-account-prompt"
    >
      <p className={styles.text}>{t('account.prompt.existing')}</p>
      {status === 'done' && (
        <p className={styles.success}>
          {t('account.prompt.loggedIn')}
          {' '}
          <Link to={ROUTE_ACCOUNT_ROOT}>{t('account.prompt.seeBookings')}</Link>
        </p>
      )}
      {status !== 'done' && !open && (
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {t('header.login')}
        </Button>
      )}
      {status !== 'done' && open && (
        <form onSubmit={submit} className="apr-account-note__form">
          <TextInput
            id="existing-account-password"
            type="password"
            value={password}
            autoComplete="current-password"
            title={t('account.prompt.passwordFor', { email })}
            placeholder={t('account.prompt.yourPassword')}
            onChange={(event) => setPassword(event.currentTarget.value)}
            errorMessage={error || undefined}
          />
          <Button
            type="submit"
            loading={status === 'sending'}
            inactive={!password}
          >
            {t('header.login')}
          </Button>
          <p className={styles.text}>
            <Link to={ROUTE_AUTH_FORGOT_PASSWORD}>
              {t('account.prompt.forgot')}
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}

/**
 * Shown to guests after booking. One tap emails them a link to set a
 * password, so account creation never stands between them and the booking.
 */
export default function CreateAccountPrompt({
  email,
}: CreateAccountPromptProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );

  const sendLink = () => {
    setStatus('sending');
    api.post('auth/forgot-password', { email })
      .then(() => {
        setStatus('sent');
        // No email and no email domain: the event itself is the signal.
        mp.track('Booking - Set Password Link Requested');
      })
      .catch(() => setStatus('error'));
  };

  return (
    <div className={styles.prompt}>
      <h4 className={styles.title}>{t('account.create.title')}</h4>
      <p className={styles.text}>
        <Trans
          t={t}
          i18nKey="account.create.body"
          values={{ email }}
          components={{ email: <span className={styles.email} /> }}
        />
      </p>
      {status === 'sent' ? (
        <p className={styles.success}>{t('account.create.sent')}</p>
      ) : (
        <Button
          variant="secondary"
          onClick={sendLink}
          loading={status === 'sending'}
        >
          {t('account.create.send')}
        </Button>
      )}
      {status === 'error' && (
        <p className={styles.error}>{t('account.create.error')}</p>
      )}
    </div>
  );
}
